import React, {
    ReactNode,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';
import { twMerge } from 'tailwind-merge';

export type OriginalPlacements =
    | 'top'
    | 'bottom'
    | 'start'
    | 'end'
    | 'bottom-start'
    | 'bottom-end'
    | 'top-start'
    | 'top-end';

// Named rather than written inline, so the generic never closes with a double
// angle bracket that some parsers read as a right shift.
type TimerId = ReturnType<typeof setTimeout>;

interface Props {
    children: ReactNode;
    label: ReactNode;
    placement?: OriginalPlacements;
    className?: string;
    tooltipClassName?: string;
}

// Hover tooltips only make sense with a precise, hovering pointer. On a touch
// device a tap synthesises a mouseenter (which would open the tooltip) but no
// mouseleave until you tap elsewhere, so it would linger stale; skip showing it
// there entirely (the trigger's aria-label still conveys the meaning).
const hasHoverPointer = (): boolean =>
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

// Keyboard focus, as opposed to focus that a click or a tap left behind.
const isKeyboardFocus = (target: EventTarget | null): boolean => {
    if (!(target instanceof Element)) {
        return false;
    }
    try {
        return target.matches(':focus-visible');
    } catch {
        // Safari learned :focus-visible in 15.4, and matches() throws on a
        // selector it cannot parse. Where the browser cannot answer, treat
        // the focus as a keyboard one rather than lose the tooltip: Safari
        // does not focus a button on tap, so the tap this test guards against
        // does not arise there.
        return true;
    }
};

export const Tooltip = ({
    children,
    label,
    placement = 'bottom',
    className = '',
    tooltipClassName = '',
}: Props) => {
    const [referenceRef, setReferenceElement] = useState(null);
    const [popperRef, setPopperElement] = useState(null);
    const [displayPopper, setDisplayPopper] = useState(false);
    const [displayTooltip, setDisplayTooltip] = useState(false);
    // Every instance used to hardcode id="tooltip", so the ids collided and
    // aria-describedby always resolved to whichever one came first in the DOM.
    const tooltipId = useId();
    // Set only for *keyboard* focus. A ref, not state: the handlers read it
    // live and it must not re-run the watcher effect.
    const keyboardFocused = useRef(false);
    // Is the pointer over the trigger right now? Read by the reveal timer when
    // it fires, so a pointer that wandered off in the meantime gets no tooltip.
    const pointerInside = useRef(false);
    // The reveal wait, and the grace period before the popper unmounts. Held in
    // refs so they can be cancelled. They used to be a debounce call built
    // inline on each use, which makes a throwaway debounced function every
    // time: nothing holds it, so nothing can cancel it, and the timer from the
    // previous interaction still runs.
    const revealTimer = useRef<TimerId>(undefined);
    const hideTimer = useRef<TimerId>(undefined);

    const clearTimers = () => {
        clearTimeout(revealTimer.current);
        clearTimeout(hideTimer.current);
        revealTimer.current = undefined;
        hideTimer.current = undefined;
    };

    // A reveal already counting down keeps its original deadline, so a pointer
    // that wobbles out and back does not restart the wait. The timer decides
    // when it fires whether the pointer is still there.
    const scheduleReveal = () => {
        if (revealTimer.current) {
            return;
        }
        revealTimer.current = setTimeout(() => {
            revealTimer.current = undefined;
            if (pointerInside.current) {
                setDisplayTooltip(true);
            }
        }, 100);
    };

    useEffect(() => clearTimers, []);

    const getPlacement = (p: Props['placement']): Placement => {
        switch (p) {
            case 'start':
                return 'left';
            case 'end':
                return 'right';
            default:
                return p;
        }
    };

    const { styles, attributes } = usePopper(referenceRef, popperRef, {
        placement: getPlacement(placement),
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 4],
                },
            },
            {
                name: 'preventOverflow',
                options: {
                    padding: 8,
                    rootBoundary: 'document',
                },
            },
        ],
    });

    // While open, close as soon as the pointer is no longer over the trigger.
    // onMouseLeave alone misses the case where the trigger moves out from under
    // a stationary cursor, such as a layout reflow (the tile relocating on
    // pin, resize, or participants joining or leaving), which would otherwise
    // strand the tooltip open. Gated on displayPopper, so a mounted but closed
    // tooltip adds nothing; only the one open tooltip carries a listener.
    useEffect(() => {
        if (!displayPopper || !referenceRef) {
            return undefined;
        }
        const closeIfPointerOutside = (event: MouseEvent) => {
            // Checked here rather than gating the whole effect: clicking a
            // control focuses it, so gating on focus would kill this watcher
            // for every mouse interaction, including the click-driven
            // reflows (pin / unpin) it exists for.
            if (keyboardFocused.current) {
                return;
            }
            const rect = referenceRef.getBoundingClientRect();
            const isInside =
                event.clientX >= rect.left &&
                event.clientX <= rect.right &&
                event.clientY >= rect.top &&
                event.clientY <= rect.bottom;
            if (!isInside) {
                pointerInside.current = false;
                clearTimeout(revealTimer.current);
                clearTimeout(hideTimer.current);
                revealTimer.current = undefined;
                hideTimer.current = undefined;
                setDisplayTooltip(false);
                setDisplayPopper(false);
            }
        };
        document.addEventListener('mousemove', closeIfPointerOutside);
        return () =>
            document.removeEventListener('mousemove', closeIfPointerOutside);
    }, [displayPopper, referenceRef]);

    // The description has to sit on the element that actually takes focus. The
    // wrapper is a roleless generic div, so a description on it is never
    // reached and never announced. Only while the tooltip is mounted, or the
    // reference dangles.
    //
    // Memoised, and above the early return so it stays an unconditional hook.
    // Cloning produces a new element, which costs the wrapped control a render:
    // without this, every tooltip render while open re-cloned and re-rendered
    // it: five times per hover rather than the two the attribute itself needs.
    const describedChildren = useMemo(
        () =>
            displayPopper && React.isValidElement(children)
                ? React.cloneElement(
                      children as React.ReactElement<{
                          'aria-describedby'?: string;
                      }>,
                      { 'aria-describedby': tooltipId },
                  )
                : children,
        [children, displayPopper, tooltipId],
    );

    // Nothing to say, nothing to wrap. Placed after the hooks above so their
    // order never changes.
    if (!label) {
        return <>{children}</>;
    }

    const handleMouseEnter = () => {
        if (!hasHoverPointer()) {
            return;
        }
        pointerInside.current = true;
        clearTimeout(hideTimer.current);
        hideTimer.current = undefined;
        setDisplayPopper(true);
        scheduleReveal();
    };

    // Focus outranks hover: moving the pointer across a control the keyboard is
    // already on must not dismiss its tooltip.
    const handleMouseLeave = () => {
        if (keyboardFocused.current) {
            return;
        }
        pointerInside.current = false;
        setDisplayTooltip(false);
        clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => setDisplayPopper(false), 100);
    };

    // Focus parity with hover (WCAG 1.4.13). React's onFocus and onBlur bubble,
    // so these fire for the wrapped control, which is the thing that actually
    // takes focus. Only the keyboard path opens it: clicking or tapping also
    // focuses, and popping a tooltip open on every tap is what the pointer
    // guard avoids.
    const handleFocus = (event: React.FocusEvent) => {
        if (!isKeyboardFocus(event.target)) {
            return;
        }
        clearTimers();
        keyboardFocused.current = true;
        setDisplayPopper(true);
        setDisplayTooltip(true);
    };

    const handleBlur = () => {
        if (!keyboardFocused.current) {
            return;
        }
        clearTimers();
        keyboardFocused.current = false;
        setDisplayTooltip(false);
        setDisplayPopper(false);
    };

    // The wrapper below is deliberately a roleless div with no tabIndex, even
    // though it carries mouse and focus handlers, so it is marked NOSONAR.
    // Static analysis reads it as a non-native interactive element and asks for
    // a role and a tab stop, but that is precisely the bug this component
    // removes: a focusable wrapper put an unlabelled, inert tab stop in front
    // of every tooltipped control. The handlers only observe hover and focus on
    // behalf of the control inside.
    return (
        <>
            <div // NOSONAR
                ref={setReferenceElement}
                className={twMerge('flex', tooltipClassName)}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={handleFocus}
                onBlur={handleBlur}
            >
                {describedChildren}
            </div>
            {displayPopper &&
                createPortal(
                    <div
                        id={tooltipId}
                        ref={setPopperElement}
                        style={styles.popper}
                        {...attributes.popper}
                        className={twMerge(
                            'z-50 max-w-xs rounded-sm bg-gray-800 px-1.5 py-0.5 text-xs/5 font-normal text-white shadow-xs transition-opacity duration-300',
                            displayTooltip ? 'opacity-100' : 'opacity-0',
                            className,
                        )}
                        role="tooltip"
                    >
                        {label}
                    </div>,
                    document.getElementById('tooltipPortal'),
                )}
        </>
    );
};

export default Tooltip;
